/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import TurndownService from 'turndown';
import MarkdownIt from 'markdown-it';

// Component imports
import ReactQuill, { Quill } from 'react-quill-new';
import QuillCustomToolbar from './QuillCustomToolbar';
import { Button, Col, Row, Space, Tooltip, Typography } from 'antd';
import { ArticleCharacterLimitIcon } from '../../../Icons/ArticleCharacterLimitIcon';
import { CopyOutlined } from '@ant-design/icons';

// Misc imports
import { RICH_TEXT_TOOLBAR_ACTIONS, RICH_TEXT_DEFAULT_TOOLBAR_ACTIONS, TURNDOWN_MARKDOWN_OPTIONS } from '../../../../constants/markdown';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import is from '../../../../utils/is';
import { MARKDOWN_LINK_REGEX } from '../../../../constants/regex';
import { ARTICLE_CHARACTER_LIMIT_TYPES } from '../../../../constants/knowledgeBases';
import { copyTextToClipboard } from '../../../../utils/function/copyToClipboard';
import InlineActionBlot from './Blots/InlineActionBlot';
import { isEmptyObject } from '../../../../utils/is/isEmptyObject';
import CustomEmojiBlot from './Blots/CustomEmojiBlot';
import registerQuillLanguageTool from '../../../../utils/misc/registerQuillLanguageTool';
import { useSpellCheckMutation } from '../../../../store/liveChat/liveChatApi';

const markdownToHTMLConverter = new MarkdownIt({ breaks: true });
// We need the following in order to override the default functionality of markdown-it
// to accept svg data url as image src. So, we can paste in img URL field (toolbar)
// an svg data url and we see the image in the markdown
// source: https://github.com/markdown-it/markdown-it/pull/769#issuecomment-920101542
markdownToHTMLConverter.validateLink = (originalValidateLink => url => {
  if (originalValidateLink(url)) {
    return true;
  }

  if (/^data:image\/svg\+xml;/.test(url)) {
    return true;
  }

  return false;
})(markdownToHTMLConverter.validateLink);

const htmlToMarkdownConverter = new TurndownService(TURNDOWN_MARKDOWN_OPTIONS);

markdownToHTMLConverter.disable('code');
markdownToHTMLConverter.disable('fence');

htmlToMarkdownConverter.addRule('convertParagraphBreaks', {
  filter: function (node) {
    return node.innerHTML.trim().toLowerCase() === '<br>';
  },
  replacement: function () {
    return '\n\n\u200B\n\n';
  }
});

htmlToMarkdownConverter.addRule('preserveBackticks', {
  filter: function(node) {
    // Check if node is a paragraph containing only backticks
    return (
      node.nodeName === 'P'
      && (node.textContent.trim() === '```' || node.textContent.trim().startsWith('```'))
    );
  },
  replacement: function(content, node) {
    // Preserve the backticks exactly as they are
    node._skipMe = true;

    return node.textContent.trim();
  }
});

htmlToMarkdownConverter.addRule('skipProcessedNodes', {
  filter: function(node) {
    return node._skipMe === true;
  },
  replacement: function() {
    return '';
  }
});
// This is a rule that should be uncommented only for debugging htmlToMarkdown
// htmlToMarkdownConverter.addRule('debugParagraphs', {
//   filter: function (node) {
//     if(node.nodeName === 'P') {
//       console.log('Processing paragraph:', {
//         innerHTML: node.innerHTML,
//         textContent: node.textContent,
//         isEmpty: !node.textContent.trim(),
//         hasBrOnly: node.innerHTML.trim().toLowerCase() === '<br>'
//       });

//     }
//     // Don't actually change anything, just pass through to other rules
//     return false;
//   },
//   replacement: function (content, node) {
//     return '';
//   }
// });

// Register custom blot and modules
Quill.register({
  'formats/inlineAction': InlineActionBlot,
  'formats/emoji': CustomEmojiBlot
}, true);
registerQuillLanguageTool(Quill);

const useToolbarId = () => {
  const [toolbarId, setToolbarId] = useState('');
  useEffect(() => {
    setToolbarId(`customToolbar-${uuidv4()}`);
  }, []);
  return toolbarId;
};

const useModules = (toolbarId, toolbarActions, spellCheckLanguage, inlineAction, setIsInlineActionActive, disableEnterDefaultBehavior, botId, handleInsertedTextWithoutFormatting) => {
  const { orgId, tenantId } = useParams();
  const { data: storyVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId: tenantId || botId });
  const [modules, setModules] = useState();
  const [spellCheck] = useSpellCheckMutation();

  useEffect(() => {
    if (toolbarId) {
      const clipboardConfig = {
        allowed: {
          tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
          attributes: ['href', 'rel', 'target', 'class', 'title']
        },
        keepSelection: false,
        substituteBlockElements: true,
        magicPasteLinks: true
      };

      if (handleInsertedTextWithoutFormatting) {
        const Delta = Quill.import('delta');
        clipboardConfig.matchers = [
          [Node.ELEMENT_NODE, (node) => {
            const text = node.textContent.trim() || '';
            return new Delta().insert(text);
          }]
        ];
        delete clipboardConfig.allowed;
      }

      setModules({
        toolbar: {
          container: `#${toolbarId}`,
          handlers: {
            link: () => (null),
            emoji: () => {}
          }
        },
        clipboard: clipboardConfig,
        // If story variables are available, include variable dropdown module
        ...(toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE)) && {
          variableDropdown: {
            storyVariables: storyVariables,
            botId: tenantId || botId
          }
        },
        languageTool: spellCheckLanguage ? {
          language: spellCheckLanguage,
          spellCheck,
          orgId,
          apiOptions: {
            enabledCategories: ['TYPOS'],  // Only check for spelling errors (and not capitalization, etc.)
            enabledOnly: 'true' // This ensures only the rules mentioned in enabledRules are checked
          }
        } : false,
        keyboard: {
          bindings: {
            slash: {
              key: 191,
              handler: function(range) {
                // Handle inline action on '/' only if it's active by embedding the blot and adding the proper class to the editor
                if (inlineAction && range?.index === 0) {
                  setIsInlineActionActive(true);
                  return false;
                }
                return true;
              }
            },
            backspace: {
              key: 8,
              handler: function(range, context) {
                if(inlineAction && range.index === 1) {
                  setIsInlineActionActive(false);
                }
                return true;
              }
            },
            enter: {
              key: 'Enter',
              handler: function(range, context) {
                if(disableEnterDefaultBehavior) {
                  return false;
                }
                return true;
              }
            },
            markdownLinks: {
              key: 32, //This indicates space key,
              handler: function(range, context) {
                const [line, offset] = this.quill.getLine(range.index);
                if (!line) {
                  return true;
                } // Safety check, if line does not exist proceed with normal space key behavior
                const textToProcess = line.domNode.textContent;
                const textToProcessStartIndex = range.index - offset;

                // Regex to match Markdown links
                const match = MARKDOWN_LINK_REGEX.exec(textToProcess);
                if (match) {
                  const matchedText = match[0];
                  const linkTextTitle = match[1];
                  const linkUrl = match[2];

                  const lastIndexOfTextToProcess = textToProcess.lastIndexOf(matchedText);
                  this.quill.deleteText(textToProcessStartIndex + lastIndexOfTextToProcess, matchedText.length);
                  this.quill.insertText(textToProcessStartIndex + lastIndexOfTextToProcess, linkTextTitle, 'link', linkUrl);

                  // Set the cursor position after the inserted link
                  this.quill.setSelection(textToProcessStartIndex + lastIndexOfTextToProcess + linkTextTitle.length);
                  return false; // Prevent the default behavior of the space key
                }
                // Default space key behavior
                return true;
              }
            }
          }
        }
      });
    }
  }, [toolbarId, storyVariables, spellCheckLanguage, inlineAction, disableEnterDefaultBehavior, botId, handleInsertedTextWithoutFormatting, orgId, spellCheck]);
  return modules;
};

// Quill editor does not support inline mode. So, we need to add the class ql-inline
const useQuillClassName = (inline) => {
  const [quillClassName, setQuillClassName] = useState('w-100');

  useEffect(() => {
    if (inline) {
      setQuillClassName(quillClassName);
    }
  }, []);

  return quillClassName;
};

/**
 * Applies the primaryTextColor format to all {{variables}} in the editor
 * @param {Object} quill The Quill instance
*/
const applyColorToVariables = (quill) => {
  if (!quill) {
    return;
  }

  // Regex to match variables in the format {{variable}}
  const variableRegex = /\{\{([^\s]+?)\}\}/g;

  // Try-catch block is needed because quill throws an error when trying to format text in some browsers
  try {
    // Clear any previous formatting related to coloring variables, so we can re-apply it
    if (quill.getFormat().primaryTextColor) {
      quill.formatText(0, quill.getLength() - 1, 'primaryTextColor', false, 'silent');
    }

    const content = quill.getText();
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
      const index = match.index;
      const length = match[0].length;
      quill.formatText(index, length, 'primaryTextColor', true, 'silent');
    }
  } catch (e) {
    return;
  }
};

const renderMarkdown = (value) => {
  return value && is.string(value) ? markdownToHTMLConverter.render(value) : '';
};

const useAutoFocus = (richEditorRef, shouldAutoFocus, index) =>
  useEffect(() => {
    if (richEditorRef && shouldAutoFocus) {
      setTimeout(function() {
        let editorElement = document.getElementsByClassName('ql-editor')[index];

        if (editorElement) {
          if (typeof window.getSelection !== 'undefined'
          && typeof document.createRange !== 'undefined') {
            var range = document.createRange();
            range.selectNodeContents(editorElement);
            range.collapse(false);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
          } else if (typeof document.body.createTextRange !== 'undefined') {
            const textRange = document.body.createTextRange();
            textRange.moveToElementText(editorElement);
            textRange.collapse(false);
            textRange.select();
          }
        }
      }, 0);
    }
  }, [richEditorRef, shouldAutoFocus]);

const useBlurOnMount = (richEditorRef, shouldAutoFocus) =>
  useEffect(() => {
    if (richEditorRef && !shouldAutoFocus) {
      const editor = richEditorRef.getEditor();
      if (editor) {
        setTimeout(() => {
          editor.blur();
        }, 0);
      }
    }
  }, [richEditorRef, shouldAutoFocus]);

// Converts HTML input to Markdown
// Uncomment the console.logs only for debugging purposes
const convertValueToMarkdown = (onChange, currentValue, originalValue, delta, source, editor) => {
  // We need to replace all non breaking spaces with space as getSemanticHTML adds non breaking spaces on every space that it reads (https://github.com/slab/quill/issues/4509)
  const markdownStep1 = htmlToMarkdownConverter.turndown(editor?.getSemanticHTML().replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 '));

  const htmlStep2 = markdownToHTMLConverter.render(markdownStep1);
  const convertedValue = htmlToMarkdownConverter.turndown(htmlStep2);

  const originalConvertedValue = htmlToMarkdownConverter.turndown(renderMarkdown(originalValue));

  // We want to trigger change when the user types or adds text formatting
  if (typeof onChange === 'function' && (source === 'user' || convertedValue !== originalConvertedValue)) {
    onChange(convertedValue, delta, source, editor, originalValue);
  }
};

const RichTextEditor = forwardRef(({
  placeholder,
  value,
  bordered = true,
  hideToolbar = false,
  isScrollable = false,
  onChange,
  onBlur,
  index = 0,
  isToolbarOpen = true,
  hasFloatingToolbar = false,
  toggleButton,
  inline = false,
  toolbarActions = Object.values(RICH_TEXT_DEFAULT_TOOLBAR_ACTIONS),
  inlineAction = {}, /*
  handleAsPlainText set to True is required for inlineAction to work properly because of markdown issues
  The inlineAction structure is:
  {
    value: string
    active: boolean
  }
    value is referencing to the key we pass to the embed where it's source is from consts RICH_TEXT_INLINE_ACTIONS
    active is referencing to whether the inlineAction embed is currently active in the RTE or not
  */
  onInlineActionChange = () => {}, // This triggers everytime the inlineAction embed is added/active or removed/inactive from RTE
  shouldAutoFocus = true,
  disabled = false,
  className = '',
  onInsertText,
  spellCheckLanguage = null,
  showCount = false,
  maxLength = 0,
  applyCharacterLimits = false,
  characterLimitOptions = {
    limits: {
      optimalCharacterLimit: 0,
      maxCharacterLimit: 0
    },
    messages: {
      optimalLimitMessage: '',
      warningLimitMessage: '',
      maxLimitMessage: ''
    }
  },
  copySourceButton = false,
  includedVariableTypes = [],
  disableEnterDefaultBehavior=false,
  botId, // To override the tenantId from the URL
  handleAsPlainText = false,
  handleInsertedTextWithoutFormatting = false,
  elementStyles = {}, // Object containing the styles to apply to the elements in the editor (e.g. { 'p': { 'color': 'red' } })
  addonAfter = null,
  ...rest
}, ref) => {
  const [richEditorRef, setRichEditorRef] = useState();
  const [quillRef, setQuillRef] = useState();
  const [isInlineActionActive, setIsInlineActionActive] = useState(inlineAction?.active || false);
  const [isAllTextSelected, setIsAllTextSelected] = useState(false);
  const toolbarId = useToolbarId();
  //TODO: If more properties like disableEnterDefaultBehavior are required to be implemented
  //      Make an options prop for useModules to encompass the required properties
  const modules = useModules(toolbarId, toolbarActions, spellCheckLanguage, !isEmptyObject(inlineAction), setIsInlineActionActive, disableEnterDefaultBehavior, botId, handleInsertedTextWithoutFormatting);
  const quillClassName = useQuillClassName(inline);
  const [blockApiChangeEvents, setBlockApiChangeEvents] = useState(!!value);
  const copySourceBtnRef = useRef(null);
  const initialPlainTextValueRef = useRef(null);

  useImperativeHandle(ref, () => {
    if(richEditorRef) {
      return {
        focus: () => {
          richEditorRef?.getEditor()?.focus();
        },
        blur: () => richEditorRef?.getEditor()?.blur()
      };
    }
  }, [richEditorRef, index]);

  const renderCharacterLimitIcon = (currentTextLength) => {

    let type = '';
    let message = '';
    let tooltipHeader = `Article size is ${currentTextLength} characters.`;
    if(currentTextLength <= characterLimitOptions.limits.optimalCharacterLimit ) {
      type = ARTICLE_CHARACTER_LIMIT_TYPES.OPTIMAL;
      message = characterLimitOptions.messages.optimalLimitMessage;
    } else if (currentTextLength <= characterLimitOptions.limits.maxCharacterLimit) {
      type = ARTICLE_CHARACTER_LIMIT_TYPES.WARNING;
      message = characterLimitOptions.messages.warningLimitMessage;
    } else {
      type = ARTICLE_CHARACTER_LIMIT_TYPES.MAX;
      message = characterLimitOptions.messages.maxLimitMessage;
    }

    return (
      <Tooltip
        placement="topLeft"
        overlayClassName="rte-article-limit-tooltip"
        title={
          <Row className="flex-row" gutter={[8, 16]}>
            <Col xs={2}>
              <ArticleCharacterLimitIcon type={type} className="fs-base mt-1"/>
            </Col>
            <Col xs={22}>
              <Row className="mt-1 ml-1" gutter={[8, 16]}>
                <Col xs={24}>
                  <Typography.Text strong>{tooltipHeader}</Typography.Text>
                </Col>
                <Col xs={24}>
                  {message}
                </Col>
              </Row>
            </Col>

          </Row>
        }>
        <ArticleCharacterLimitIcon type={type} className="fs-lg"/>
      </Tooltip>
    );
  };

  /**
   * The following useEffect is used in order to apply `elementStyles` property.
   * We iterate through the html elements of the editor and apply the respective style from `elementStyles` if any
   */
  useEffect(() => {
    if (quillRef) {
      try {
        const editor = quillRef;

        if (elementStyles && Object.keys(elementStyles).length) {
          Object.entries(elementStyles).forEach(([element, styles]) => {
            const elements = editor.root.querySelectorAll(element);

            elements.forEach(el => {
              Object.entries(styles).forEach(([property, value]) => {
                // Convert camelCase to kebab-case for CSS properties
                const cssProperty = property.replace(/([A-Z])/g, '-$1').toLowerCase();
                el.style.setProperty(cssProperty, value, 'important');
              });
            });
          });
        }
      } catch(err) {
        console.log(err);
      }

    }
  }, [quillRef, elementStyles]);

  // Handle blots and modules
  useEffect(() => {
    require('./Blots/CustomLinkBlot');
    require('./Modules/QuillPasteSmartModule');

    if (toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE)) {
      require('./Modules/VariableDropdownModule');
      require('./Blots/PrimaryTextBlot');
    }
  }, [toolbarActions]);

  // When the editor text changes, reassess whether the text contains variables and color them
  useEffect(() => {
    try {
      if (quillRef) {
        applyColorToVariables(quillRef);
      }

      /** This block's sole purpose is to syncronize RTE's internal value and selection
          with external value changes.
          [Example]
          In HTTP Request node where we want to changes in the URL params form fields
          to reflect on the URL field which is RTE based. This external change in the value happens in the parent component
      */
      if (richEditorRef) {
        const editor = richEditorRef.getEditor();
        const currentContent = editor.getText().trim();

        if (handleAsPlainText && currentContent !== value) {
          const { index } = editor.getSelection() || { index: editor.getLength() };
          editor.setText(value || '', 'user');
          editor.setSelection(index, 0, 'user');
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [value, richEditorRef, handleAsPlainText, quillRef]);

  useAutoFocus(richEditorRef, shouldAutoFocus, index);
  useBlurOnMount(richEditorRef, shouldAutoFocus);

  // Clear history after initial setup to prevent removing the initial content when Ctrl + Z (history includes the initial content setup)
  useEffect(() => {
    if (quillRef) {
      quillRef.history.clear();
    }
  }, [quillRef]);

  /**
   * Inserts text at the current cursor position
   * @param {String} text The text to insert
   * @param {number} position The position to insert the text at
   * @param {boolean} skipSlash Remove '/' in case of triggering a slash command (/action, /answer)
   * @param {boolean} replace Option to replace the entire text
   */
  const insertText = (text, position, skipSlash, replace) => {
    if (richEditorRef && quillRef) {
      const editor = quillRef;
      const currentText = editor.getText();
      if (skipSlash && currentText?.[0] === '/') {
        editor.deleteText(0, editor.getLength());
      }

      if (replace) {
        editor.setText('');
      }

      position = typeof position === 'number' ? position : editor.getSelection(true);
      editor.insertText(position, text);
    }
  };

  useEffect(() => {
    if (onInsertText) {
      onInsertText(insertText); // If prop is provided, set the provided function to use our local insertText function
    }
  }, [onInsertText]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (isAllTextSelected) {
        setIsAllTextSelected(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAllTextSelected]);

  // It returns markdown length instead of just string length to conform with backend character restrictions
  const getTextLength = (editor) => {
    const unprivilegedEditor = richEditorRef?.makeUnprivilegedEditor(editor);
    const htmlValue = unprivilegedEditor?.getHTML?.();
    let convertedValue = '';
    if(htmlValue){
      convertedValue = htmlToMarkdownConverter?.turndown(markdownToHTMLConverter?.render(htmlToMarkdownConverter?.turndown(htmlValue)));
    }
    return convertedValue?.length;
  };

  /**
   * It adheres to current maxLength to achieve:
   *  1. Do not allow a normal keyboard event to go through if maxLength is reached
   *  2. Only allow deleting characters if maxLength is reached
   * @param {KeyboardEvent} keyboardEvent the current keyboard event
   * @param {*} editor the current rich text editor
   * @param {Number} maxLength the current maxLength limit to adhere to
   */
  const maxLengthInputControl = (keyboardEvent, editor, maxLength) => {
    const currentLength = getTextLength(editor);
    const isDeleteOrBackspace = is.deleteEvent(keyboardEvent) || is.backspaceEvent(keyboardEvent);

    if (currentLength >= maxLength && !isDeleteOrBackspace) {
      const selection = editor?.getSelection();
      if (!selection || selection.length === 0) {
        keyboardEvent.preventDefault();
      }
    }
  };
  useEffect(() => {
    try {
      const editor = quillRef;
      if (!editor || isEmptyObject(inlineAction)) {
        return;
      }
      const editorContents = editor.getContents();

      // Apply class changes to sync with state
      if (isInlineActionActive) {
        editor.root.classList.add('inline-action-active');
        // Check if the inline action embed already exists
        const hasInlineActionEmbed = editorContents.ops?.some(op =>
          op.insert && typeof op.insert === 'object' && 'inlineAction' in op.insert
        );

        // Insert the embed only if it doesn't exist yet
        if (!hasInlineActionEmbed) {
          editor.insertEmbed(0, 'inlineAction', inlineAction?.value, 'silent');
          setTimeout(() => {
            editor?.setSelection(editor.getLength());
          }, 50);
        }
      } else {
        editor.root.classList.remove('inline-action-active');
        // Check if the inline action embed exists
        const inlineActionIndex = editorContents.ops?.findIndex(op =>
          op.insert && typeof op.insert === 'object' && 'inlineAction' in op.insert
        );

        // If it exists, remove it
        if (inlineActionIndex !== -1 && inlineActionIndex !== undefined) {
          editor.deleteText(inlineActionIndex, 1);
        }
      }

      onInlineActionChange(isInlineActionActive);
    } catch(e) {
      console.log(e);
    }

    return () => {
      setIsAllTextSelected(false);
    };
  }, [isInlineActionActive, richEditorRef, inlineAction, onInlineActionChange]);

  return (
    <div
      className={classNames('ql-container', {
        'ql-has-floating-toolbar': hasFloatingToolbar,
        'ql-has-toggle-button': toggleButton,
        'ql-has-addon-after': addonAfter,
        'ql-inline': inline,
        [className]: className
      })}
    >
      <Space.Compact block>
        <div
          data-count={showCount ? `${getTextLength(quillRef)} ${maxLength ? `/${maxLength}` : ''}` : ''}
          className={classNames('position-relative w-100', {
            'ql-show-count': showCount
          })}
          style={ { maxWidth: inline ? '450px' : '' }}
          onMouseEnter={() => {
            if (!copySourceBtnRef.current) {
              return;
            }
            copySourceBtnRef?.current?.classList?.add('d-block');
            copySourceBtnRef?.current?.classList?.remove('d-none');
          }}
          onMouseLeave={() => {
            if (!copySourceBtnRef.current) {
              return;
            }
            copySourceBtnRef?.current?.classList?.remove('d-block');
            copySourceBtnRef?.current?.classList?.add('d-none');
          }}
        >
          {
            applyCharacterLimits && (
              <div className="position-absolute z-index-popover" style={{ top: '10px', right: '10px' }}>
                {renderCharacterLimitIcon(getTextLength(quillRef))}
              </div>
            )
          }

          {
            copySourceButton
              ? <Button
                ref={copySourceBtnRef}
                icon={<CopyOutlined className="fs-sm"/>}
                type="primary"
                size="small"
                style={{ right: 10, top: 50 }}
                onClick={() => {
                  if (!richEditorRef) {
                    return;
                  }
                  copyTextToClipboard(value);
                }}
                className="position-absolute z-index-popover d-none shadow p-0">
              </Button>
              : null
          }

          {
            toolbarId ? (
              <QuillCustomToolbar
                className={classNames({
                  'ql-toolbar ql-snow': isToolbarOpen,
                  'd-none': !isToolbarOpen || hideToolbar,
                  'ql-disabled': disabled,
                  'border-radius-bottom-right-0': addonAfter
                })}
                id={toolbarId}
                richEditorRef={richEditorRef}
                quillRef={quillRef}
                toolbarActions={toolbarActions}
                includedVariableTypes={includedVariableTypes}
                botId={botId}
              />
            ) : null
          }
          {
            modules ? (
              <ReactQuill
                readOnly={disabled}
                theme="snow"
                ref={(el) => {
                  setRichEditorRef(el);
                  try {
                    const editorInstance = el?.getEditor();
                    if (editorInstance) {
                      editorInstance.focus = function () {
                        this.selection.focus();
                      }.bind(editorInstance);
                      setQuillRef(editorInstance);
                    } else {
                      setQuillRef(null);
                    }
                  } catch (err) {
                    console.log(err);
                    setQuillRef(null);
                  }
                }}
                placeholder={placeholder}
                onKeyDown={(event) => {
                  if(isAllTextSelected && (event.keyCode === 46 || event.keyCode === 8)) {
                    setIsInlineActionActive(false);

                    if (richEditorRef && quillRef) {
                      const editor = quillRef;
                      editor.setText('');
                    }
                  }
                  if(event.ctrlKey && event.key === 'a') {
                    setIsAllTextSelected(true);
                  } else {
                    if(isAllTextSelected) {
                      setIsAllTextSelected(false);
                    }
                  }

                  if (maxLength > 0) {
                    const editor = quillRef;
                    maxLengthInputControl(event, editor, maxLength);
                  }
                }}
                defaultValue={renderMarkdown(value)}
                className={classNames(quillClassName, {
                  'ql-notoolbar': !isToolbarOpen,
                  'ql-border-0': !bordered,
                  'ql-editor-scrollable': isScrollable
                })}
                modules={modules}
                onChange={(currentValue, delta, source, editor) => {
                // Prevent the initial trigger of onChange https://github.com/zenoamaro/react-quill/issues/259#issuecomment-506436291
                // if source is silent then return (developer sets a silent operation, check variable format)
                  if (source === 'silent') {
                    return;
                  }

                  if (source === 'api' && blockApiChangeEvents) {
                    setBlockApiChangeEvents(false);
                    return;
                  }

                  // If `handleAsPlainText` prop is set to `true` then handle the rte's value as plain text
                  if (handleAsPlainText) {
                    let currentPlainText = editor.getText().trim();

                    // Initialize the reference value only once
                    if(initialPlainTextValueRef.current === null) {
                      initialPlainTextValueRef.current = value ? value.trim() : '';
                    }

                    // Only skip if the text hasn't changed AND we're not starting from empty
                    if (currentPlainText === initialPlainTextValueRef.current && initialPlainTextValueRef.current !== '') {
                      return;
                    }

                    // Update the reference to the current value
                    initialPlainTextValueRef.current = currentPlainText;
                    onChange(currentPlainText, delta, source, editor, value);
                    return;
                  }

                  convertValueToMarkdown(onChange, currentValue, value, delta, source, editor);
                }}
                onBlur={onBlur}
                {...rest}
              />
            ) : null
          }

          {toggleButton}
        </div>
        {addonAfter}
      </Space.Compact>
    </div>

  );
});

RichTextEditor.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  bordered: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  index: PropTypes.number,
  isToolbarOpen: PropTypes.bool,
  toolbarActions: PropTypes.array,
  toggleButton: PropTypes.element,
  inline: PropTypes.bool,
  shouldAutoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  onInsertText: PropTypes.func,
  inlineAction: PropTypes.object,
  onInlineActionChange: PropTypes.func,
  spellCheckLanguage: PropTypes.string,
  showCount: PropTypes.bool,
  maxLength: PropTypes.number,
  includedVariableTypes: PropTypes.array,
  botId: PropTypes.string,
  isScrollable: PropTypes.bool,
  hasFloatingToolbar: PropTypes.bool,
  handleAsPlainText: PropTypes.bool,
  elementStyles: PropTypes.object,
  characterLimitOptions: PropTypes.object,
  applyCharacterLimits: PropTypes.bool,
  copySourceButton: PropTypes.bool,
  disableEnterDefaultBehavior: PropTypes.bool,
  addonAfter: PropTypes.element,
  handleInsertedTextWithoutFormatting: PropTypes.bool
};

export default RichTextEditor;
